import React from "react"
import moment from "moment"
import {
  compose,
  withProps,
  branch,
  withHandlers,
  withStateHandlers,
} from "recompact"
import { withPropsOnChange as withPropsOnChangeOrig } from "recompose"
import allEventTypes from "src/hocs/allEventTypes"
import entityApi from "src/hocs/entity"
import { selectViolationsList } from "src/engine/modules/validate/selectors"
import Calendar from "src/components/Calendar"
import { submit } from "redux-form"
import workspace from "src/hocs/workspace"
import withQueryParams from "src/hocs/withQueryParams"
import queryString from "query-string"
import { connect } from "react-redux"
import lang from "src/lang"
import EventItem, { FORM_NAME } from "src/containers/Events/Item"
import SidebarHeader from "src/components/Calendar/SidebarHeader"
import eventModel from "src/models/calendar/models/event"
import planModel from "src/models/plan"
import planEventModel from "src/models/plan/models/event"
import withSideModalPending from "src/hocs/withSideModalPending"
import PropTypes from "prop-types"
import get from "lodash/get"
import { selectors as entitySelectors } from "src/engine/modules/entity"
import CrudModal from "src/components/CrudModal"
import storage from "src/helpers/storage"
import hocInterface from "src/helpers/hocInterface"
import { PLANS_URL, CALENDAR_URL } from "src/constants/routeNames"
import { useQueryState } from "src/hooks"
import withEventPermissions from "./hocs/withEventPermissions"
import withTimeIntervalQuery from "./hocs/withTimeIntervalQuery"
import ScaleControl from "./components/ScaleControl"
import PlanInfoPanel from "./components/PlanInfoPanel"
import buildCurrentTime from "./hocs/buildCurrentTime"
import withItemCreation from "./hocs/withItemCreation"
import withItemEditing from "./hocs/withItemEditing"
import withPlanTimeRangeRedirect from "./hocs/withPlanTimeRangeRedirect"
import withPlanResources from "./hocs/withPlanResources"
import withGroupsAndItems from "./hocs/withGroupsAndItems"
import withCalendar, { ORDER_ASC } from "./hocs/withCalendar"
import getScrollLimit from "./helpers/getScrollLimit"
import { saveScrollState, getMaxZoom } from "./helpers"

import {
  SCALE_CODES,
  SCALE_TIMESTEPS,
  SCALE_ZOOMS,
  URL_DATE_FORMAT,
  LAST_SCROLL_POSITIONS,
  LAST_SELECTED_DATES,
  YEAR,
} from "./constants"

const EnhancedCalendar = compose(
  branch(
    ({ plan, currentScale }) =>
      !get(plan, `data.locked`) && currentScale !== YEAR,
    compose(
      branch(
        ({ isEventCreationEnabled }) => isEventCreationEnabled,
        withItemCreation,
      ),
      withItemEditing,
    ),
  ),
  branch(({ plan }) => Boolean(plan), withPlanResources),
)(Calendar)

const closeModal = ({
  history,
  queryParams,
  planId,
  match: {
    params: { scale, date },
  },
}) => () => {
  if (planId) {
    history.push(
      `${PLANS_URL}/${planId}/calendar/${scale}/${date}?${queryString.stringify(
        queryParams,
      )}`,
    )
  } else {
    history.push(
      `${CALENDAR_URL}/${scale}/${date}?${queryString.stringify(queryParams)}`,
    )
  }
}

const closeModalAfterEventCreation = ({
  history,
  location: { search },
  planId,
  match: {
    params: { scale },
  },
  event: {
    data: { startDateTime },
    createdId,
  },
}) => () => {
  const eventStartDate = moment(startDateTime)
    .startOf(scale)
    .format(URL_DATE_FORMAT)

  if (planId) {
    history.push(
      `${PLANS_URL}/${planId}/calendar/${scale}/${eventStartDate}/events/${createdId}${search ||
        ``}`,
    )
  } else {
    history.push(
      `${CALENDAR_URL}/${scale}/${eventStartDate}/events/${createdId}${search ||
        ``}`,
    )
  }
}

const enhance = hocInterface(
  {
    consumeProps: {
      location: PropTypes.object,
      match: PropTypes.object,
      history: PropTypes.object,
      planId: PropTypes.string,

      // Taken from route params
      date: PropTypes.string,
      scale: PropTypes.string,
    },
    exposeProps: [
      `orderBy`,
      `groupsCache`,
      `totalCount`,
      `items`,
      `calendarStartTime`,
      `calendarEndTime`,
      `momentCalendarStartTime`,
      `momentCalendarEndTime`,
      `calendarScale`,
      `queryApi`,
      `workspace`,
      `match`,
      `eventTypes`,
      `currentTimeText`,
      `visibleTimeStart`,
      `visibleTimeEnd`,
      `location`,
      `history`,
      `plan`,
      `SortOrFiltersChanged`,
      `eventFromStackData`,
      `eventgroupPosition`,
      `toggleOrder`,
      `resourceTypeIdFilter`,
      `resourceNameFilter`,
      `eventTypeIdFilter`,
      `statusCodeFilter`,
      `eventCustomFieldFilter`,
      `filterCalendarBy`,
      `onTimeChange`,
      `submitForm`,
      `resetForm`,
      `loadNextPage`,
      `addEventData`,
      `handleEventAdd`,
      `handleEventRemove`,
      `handleEventEdit`,
      `isEventCreationEnabled`,
      `canEditEvent`,
      `canDeleteEvent`,
      `canChangeStatus`,
      `calendarMeta`,
      `calendarData`,
    ],
  },
  compose(
    BaseComponent => props => {
      const {
        query: { filter },
        handlers: { setQuery },
      } = useQueryState()
      return (
        <BaseComponent
          {...props}
          calendarFilter={filter}
          setFilter={setQuery}
        />
      )
    },

    withTimeIntervalQuery,

    withCalendar,

    /* Forms plan id, if exists (if plan is not exists, then id will be undefined ) */
    withProps(({ planId }) => ({ id: planId })),

    /* Fetch plan itself if planId exists */
    branch(
      ({ planId }) => !!planId,
      compose(
        entityApi({
          to: `plan`,
          model: planModel,
          idPropName: `planId`,
        }),
        withSideModalPending(`plan`, `План не найден`, true),
      ),
    ),

    // If plan and it's data exists, use redirect hoc
    branch(
      ({ plan, planId }) => Boolean(planId) && Boolean(get(plan, `data`)),
      withPlanTimeRangeRedirect,
    ),

    /* Fetch all event types */
    // hocInterface нужен, чтобы allEventTypes не съедал orderBy
    hocInterface({ exposeProps: [`eventTypes`] }, allEventTypes()),

    connect((state, { planId }) => ({
      eventModelData: entitySelectors.model(
        state,
        planId ? planEventModel : eventModel,
      ),
      planAllEventsValidate: selectViolationsList(state, `eventViolations`),
    })),

    withStateHandlers(
      {
        eventFromStackData: null,
        eventgroupPosition: null,
      },
      {
        addEventData: () => (data, groupPosition) => ({
          eventFromStackData: data,
          eventgroupPosition: groupPosition,
        }),
      },
    ),

    withQueryParams(),

    withHandlers({
      handleSubmitForm: ({ dispatch }) => () => dispatch(submit(FORM_NAME)),
      handleResetForm: ({
        history,
        queryParams,
        planId,
        match: {
          params: { date, scale },
        },
      }) => () => {
        const searchQuery = queryString.stringify(queryParams)
        if (planId) {
          history.push(
            `${PLANS_URL}/${planId}/calendar/${scale}/${date}?${searchQuery}`,
          )
        } else {
          history.push(`/calendar/${scale}/${date}?${searchQuery}`)
        }
      },
      handleNewItem: ({
        planId,
        history,
        queryParams,
        match: {
          params: { date, scale },
        },
      }) => () => {
        const searchQuery = queryString.stringify(queryParams)
        if (planId) {
          history.push(
            `${PLANS_URL}/${planId}/calendar/${scale}/${date}/events/new?${searchQuery}`,
          )
        } else {
          history.push(`/calendar/${scale}/${date}/events/new?${searchQuery}`)
        }
      },
    }),
    withGroupsAndItems,
    /* Some logic to control timeline */
    /*
     * Использован recompose из-за бага с recompact https://github.com/neoziro/recompact/issues/91
     * Функция не создавалась из-за того, что plan = undefined
     *
     * Вообще этой функции требуется рефакторинг, уточнение как и зачем она работает
     * Сейчас она работает не совсем правильно
     */
    withPropsOnChangeOrig(
      [`plan`],
      ({ disableVisibleTimeStart, onBoundedTimeChange, plan }) => ({
        onTimeChange: params => {
          const { start, end, updateScrollCanvas, scale } = params

          let nextStart = start
          let nextEnd = end

          // Fix wrong start value that comes from timeline lib sometimes
          if (nextStart > nextEnd) {
            nextStart = nextEnd - scale
          }

          const scrollLimit = getScrollLimit(params, plan)
          const diff = end - start

          // Apply scroll limits
          nextStart =
            nextStart <= scrollLimit.start ? scrollLimit.start : nextStart
          nextEnd = nextEnd >= scrollLimit.end ? scrollLimit.end : nextEnd
          // Fix scroll with limited border, otherwise it will be treated as zoom event
          if (nextStart === scrollLimit.start && nextEnd !== scrollLimit.end) {
            nextEnd = nextStart + diff
          }
          if (nextEnd === scrollLimit.end && nextStart !== scrollLimit.start) {
            nextStart = nextEnd - diff
          }

          // Further timeline processing
          disableVisibleTimeStart()
          onBoundedTimeChange(nextStart, nextEnd)
          updateScrollCanvas(nextStart, nextEnd)
          saveScrollState(SCALE_CODES[scale], {
            start: parseInt(nextStart, 10),
            end: parseInt(nextEnd, 10),
          })
        },
      }),
    ),

    /* Add workspace to the current props flow */
    workspace,

    /* Specify form API */
    connect(
      null,
      (
        dispatch,
        { queryParams, handleSubmitForm, handleResetForm, handleNewItem },
      ) => {
        const searchQuery = queryString.stringify(queryParams)
        return {
          submitForm: handleSubmitForm,
          resetForm: handleResetForm,
          newItem: handleNewItem,
          searchQuery,
          dispatch,
        }
      },
    ),

    /* Build block which displays current time and gives UI to change it
     * Provides `currentTimeText` property. */
    buildCurrentTime,

    withEventPermissions,
  ),
)

export default enhance(
  ({
    toggleOrder,
    orderBy,
    resourceTypeIdFilter,
    resourceNameFilter,
    eventCustomFieldFilter,
    eventTypeIdFilter,
    statusCodeFilter,
    filterCalendarBy,
    groupsCache,
    totalCount,
    items,
    calendarStartTime,
    calendarEndTime,
    momentCalendarStartTime,
    momentCalendarEndTime,
    calendarScale,
    onTimeChange,
    submitForm,
    resetForm,
    queryApi,
    workspace: {
      labels: { pending, invalid, dirty },
    },
    match: {
      params: { id, planId },
    },
    queryApi: { push },
    eventTypes: { pending: eventTypesPending, data: eventTypesData },
    currentTimeText,
    visibleTimeStart,
    visibleTimeEnd,
    location,
    history,
    plan,
    loadNextPage,
    SortOrFiltersChanged,
    eventFromStackData,
    eventgroupPosition,
    addEventData,
    handleEventAdd,
    handleEventRemove,
    handleEventEdit,
    isEventCreationEnabled,
    canEditEvent,
    canDeleteEvent,
    canChangeStatus,
    calendarMeta,
    calendarData,
  }) => {
    return (
      <>
        <div>
          <div>
            <EnhancedCalendar
              groupsCache={groupsCache}
              items={items}
              eventTypes={eventTypesData}
              headerLabelHeight={56}
              resourceTypeIdFilter={resourceTypeIdFilter}
              resourceNameFilter={resourceNameFilter}
              eventCustomFieldFilter={eventCustomFieldFilter}
              eventTypeIdFilter={eventTypeIdFilter}
              statusCodeFilter={statusCodeFilter}
              filterCalendarBy={filterCalendarBy}
              eventFromStackData={eventFromStackData}
              eventgroupPosition={eventgroupPosition}
              sidebarContent={SidebarHeader({
                sortOrder: orderBy === ORDER_ASC ? `asc` : `desc`,
                groupsCount: totalCount,
                toggleSortOrder: toggleOrder,
                pending: calendarMeta.status.pending,
              })}
              itemRenderer={({
                item,
                timelineContext,
                getItemProps,
                onSelect,
                selected,
                getResizeProps,
              }) => (
                <item.Component
                  getItemProps={getItemProps}
                  onSelect={onSelect}
                  selected={selected}
                  item={item}
                  timelineContext={timelineContext}
                  getResizeProps={getResizeProps}
                  addEventData={addEventData}
                />
              )}
              onAdd={() => {
                const {
                  location: { pathname },
                } = history
                push(`${pathname}/events/new`)
              }}
              onBack={() => {
                const previousTimePoint = moment(calendarStartTime).subtract(
                  1,
                  SCALE_CODES[calendarScale],
                )

                // Reset saved dates to avoid ridiculous dates jumps when changing scale
                storage.remove(LAST_SELECTED_DATES)
                storage.remove(LAST_SCROLL_POSITIONS)
                push(
                  window.location.pathname.replace(
                    /\/calendar\/?[^/]*\/?[^/]*/,
                    `/calendar/${
                      SCALE_CODES[calendarScale]
                    }/${previousTimePoint
                      .startOf(SCALE_CODES[calendarScale])
                      .format(URL_DATE_FORMAT)}`,
                  ),
                )
              }}
              onForward={() => {
                const previousTimePoint = moment(calendarStartTime).add(
                  1,
                  SCALE_CODES[calendarScale],
                )

                // Reset saved dates to avoid ridiculous dates jumps when changing scale
                storage.remove(LAST_SELECTED_DATES)
                storage.remove(LAST_SCROLL_POSITIONS)
                push(
                  window.location.pathname.replace(
                    /\/calendar\/?[^/]*\/?[^/]*/,
                    `/calendar/${
                      SCALE_CODES[calendarScale]
                    }/${previousTimePoint
                      .startOf(SCALE_CODES[calendarScale])
                      .format(URL_DATE_FORMAT)}`,
                  ),
                )
              }}
              isBackDisabled={
                Boolean(get(plan, `data.endDateTime`)) &&
                moment(calendarStartTime)
                  .subtract(1, SCALE_CODES[calendarScale])
                  .isBefore(
                    moment(get(plan, `data.startDateTime`)).startOf(
                      SCALE_CODES[calendarScale],
                    ),
                  )
              }
              isForwardDisabled={
                Boolean(get(plan, `data.endDateTime`)) &&
                moment(calendarStartTime)
                  .add(1, SCALE_CODES[calendarScale])
                  .isAfter(
                    moment(get(plan, `data.endDateTime`) - 1).endOf(
                      SCALE_CODES[calendarScale],
                    ),
                  )
              }
              defaultTimeStart={momentCalendarStartTime}
              defaultTimeEnd={momentCalendarEndTime}
              queryApi={queryApi}
              visibleTimeStart={visibleTimeStart}
              visibleTimeEnd={visibleTimeEnd}
              calendarStartTime={calendarStartTime}
              calendarEndTime={calendarEndTime}
              onTimeChange={onTimeChange}
              maxZoom={getMaxZoom(calendarScale, calendarStartTime)}
              minZoom={SCALE_ZOOMS[calendarScale].min}
              timeSteps={SCALE_TIMESTEPS[calendarScale]}
              displayLoaderOverlay={
                !calendarData.length && calendarMeta.status.pending
              }
              pending={
                pending !== 0 ||
                calendarMeta.status.pending ||
                eventTypesPending
              }
              topDockContent={
                planId && (
                  <PlanInfoPanel
                    startDateTime={get(plan, `data.startDateTime`)}
                    endDateTime={get(plan, `data.endDateTime`)}
                    name={get(plan, `data.name`)}
                    onBack={() => {
                      const backLocation = get(location, `state.from`)
                      if (backLocation) {
                        history.push(backLocation)
                      } else {
                        history.push({
                          ...location,
                          pathname: PLANS_URL,
                          search: ``,
                        })
                      }
                    }}
                  />
                )
              }
              centerDockContent={currentTimeText}
              rightDockContent={
                <ScaleControl
                  calendarScale={calendarScale}
                  start={calendarStartTime}
                  queryApi={queryApi}
                />
              }
              currentScale={calendarScale}
              loadNextPage={loadNextPage}
              SortOrFiltersChanged={SortOrFiltersChanged}
              handleEventAdd={handleEventAdd}
              handleEventRemove={handleEventRemove}
              onEventEdited={handleEventEdit}
              plan={plan}
              onCreate={closeModal}
              planId={planId}
              isEventCreationEnabled={isEventCreationEnabled}
              canEditEvent={canEditEvent}
              canDeleteEvent={canDeleteEvent}
              canChangeStatus={canChangeStatus}
            />
          </div>
        </div>
        <CrudModal
          id={id}
          submitHandler={submitForm}
          resetHandler={resetForm}
          dirty={dirty}
          invalid={invalid}
          pending={pending}
          isOpen={!!id}
          modelName="events"
          fullHeight
          fullWidth
          title={
            id === `new`
              ? (plan && lang.addEventToPlan) || lang.addEventToCalendar
              : lang.editEvent
          }
        >
          {({ overrideModalConf, resetModalConf }) => (
            <EventItem
              id={id === `new` ? false : id}
              planId={planId}
              onCreate={closeModalAfterEventCreation}
              onUpdate={closeModal}
              overrideModalConf={overrideModalConf}
              resetModalConf={resetModalConf}
              canEdit={canEditEvent}
            />
          )}
        </CrudModal>
      </>
    )
  },
)
